import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Building2, ArrowLeft, User, Phone, Mail, Calendar, Link as LinkIcon, Pencil, Trash2, X, Check, FileText, BarChart3, Users, Plus, ArrowRight } from 'lucide-react';
import { Client, Contact, Collab, getDocuments, updateDocument, deleteDocument, getDocument } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContextSupabase';
import { Dialog } from '@headlessui/react';

const BUSINESS_SECTORS = [
  'Aéronautique & Spatial',
  'Agriculture & Agro-alimentaire',
  'Ameublement & Décoration',
  'Architecture & BTP',
  'Art & Culture',
  'Assurance',
  'Automobile',
  'Banque & Finance',
  'Beauté & Cosmétiques',
  'Bijouterie & Joaillerie',
  'Brasserie & Boissons alcoolisées',
  'Cafés & Restaurants',
  'Chimie & Pharmacie',
  'Commerce & Distribution',
  'Communication & Marketing',
  'Conseil & Services aux entreprises',
  'Cybersécurité',
  'Design',
  'E-commerce',
  'Éducation & Formation',
  'Électronique',
  'Énergie & Environnement',
  'Événementiel',
  'Film & Production audiovisuelle',
  'Fitness & Bien-être',
  'Food & Beverage',
  'Gaming & Esport',
  'Hôtellerie',
  'Immobilier',
  'Industrie',
  'Informatique & Software',
  'Jeux & Jouets',
  'Juridique',
  'Lifestyle',
  'Logistique & Transport',
  'Luxe',
  'Média & Presse',
  'Mode & Textile',
  'Musique',
  'Nautisme',
  'Petfood & Animaux',
  'Retail',
  'Santé & Médical',
  'Services à la personne',
  'Sport & Équipements sportifs',
  'Technologie & High-tech',
  'Télécommunications',
  'Tourisme & Voyage',
  'Autre'
];

export default function ClientDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState<Client | null>(null);
  const [collabs, setCollabs] = useState<Collab[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const { userProfile } = useAuth();
  const [formData, setFormData] = useState<Partial<Client>>({
    brand_name: '',
    contacts: [],
    sector: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (!id) {
        setError('ID client non spécifié');
        setIsLoading(false);
        return;
      }

      // Charger les données du client
      const clientData = await getDocument<Client>('clients', id);
      if (!clientData) {
        setError('Client non trouvé');
        setIsLoading(false);
        return;
      }

      setClient(clientData);
      setFormData({
        brand_name: clientData.brand_name,
        contacts: clientData.contacts ? [...clientData.contacts] : [],
        sector: clientData.sector || '',
        notes: clientData.notes
      });

      // Charger les collaborations liées à ce client
      const collabsData = await getDocuments<Collab>('collabs', userProfile?.agency_id || '');
      const clientCollabs = collabsData.filter(collab => collab.brand === clientData.brand_name);
      
      // Trier par date (plus récentes en premier)
      clientCollabs.sort((a, b) => {
        const dateA = a.date ? 
          (typeof a.date === 'object' && 'seconds' in a.date) ? 
            a.date.seconds * 1000 : 
            new Date(a.date).getTime() : 
          0;
        
        const dateB = b.date ? 
          (typeof b.date === 'object' && 'seconds' in b.date) ? 
            b.date.seconds * 1000 : 
            new Date(b.date).getTime() : 
          0;
        
        return dateB - dateA;
      });
      
      setCollabs(clientCollabs);
    } catch (error) {
      console.error('Error loading client data:', error);
      setError('Erreur lors du chargement des données');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      if (!id) return;

      await updateDocument('clients', id, formData);
      setSuccess('Client mis à jour avec succès');
      await loadData();
      setIsEditModalOpen(false);
    } catch (error) {
      console.error('Error updating client:', error);
      setError('Erreur lors de la mise à jour du client');
    }
  };

  const handleDelete = async () => {
    try {
      if (!id) return;

      await deleteDocument('clients', id);
      setSuccess('Client supprimé avec succès');
      setTimeout(() => {
        navigate('/app/clients');
      }, 1500);
    } catch (error) {
      console.error('Error deleting client:', error);
      setError('Erreur lors de la suppression du client');
    }
  };

  const handleAddContact = () => {
    setFormData(prev => ({
      ...prev,
      contacts: [...(prev.contacts || []), {
        name: '',
        email: '',
        phone: '',
        type: 'brand',
        role: ''
      }]
    }));
  };

  const handleRemoveContact = (index: number) => {
    setFormData(prev => ({
      ...prev,
      contacts: prev.contacts?.filter((_, i) => i !== index) || []
    }));
  };

  const handleContactChange = (index: number, field: keyof Contact, value: string) => {
    setFormData(prev => ({
      ...prev,
      contacts: prev.contacts?.map((contact, i) => 
        i === index ? { ...contact, [field]: value } : contact
      ) || []
    }));
  };

  const formatDate = (date: Date | { seconds: number } | string | undefined) => {
    if (!date) return '';
    
    // Si c'est une chaîne de caractères (format ISO), la convertir en Date
    if (typeof date === 'string') {
      const parsedDate = new Date(date);
      if (isNaN(parsedDate.getTime())) return 'Date invalide';
      return parsedDate.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
    
    // Si c'est un objet avec une propriété 'seconds' (Firebase Timestamp)
    if (typeof date === 'object' && date !== null && 'seconds' in date) {
      return new Date(date.seconds * 1000).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
    
    // Sinon, traiter comme un objet Date
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(num)) return '0 €';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(num);
  };

  const getCampaignStatus = (campaign: Collab) => {
    if (campaign.payment_received) return { label: 'Terminée', className: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/20' };
    if (campaign.contract_signed) return { label: 'En cours', className: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/20' };
    if (campaign.quote_sent) return { label: 'Devis envoyé', className: 'bg-blue-500/20 text-blue-400 border-blue-500/20' };
    return { label: 'En attente', className: 'bg-white/[0.08] text-white/50 border-white/[0.08]' };
  };

  const getTotalRevenue = () => {
    return collabs.reduce((total, collab) => {
      const collabTotal = collab.influencers?.reduce((sum, inf) => 
        sum + parseFloat(inf.rate || '0'), 0) || 0;
      return total + collabTotal;
    }, 0);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-white/10 border-t-white/60"></div>
      </div>
    );
  }

  if (error && !client) {
    return (
      <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-5 py-4 rounded-xl">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {success && (
        <div className="bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 px-5 py-4 rounded-xl font-medium">
          {success}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/app/clients')}
            className="p-2.5 rounded-xl bg-white/[0.06] hover:bg-white/[0.10] border border-white/[0.08] transition-all"
          >
            <ArrowLeft className="w-5 h-5 text-white/60" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-white">{client?.brand_name}</h1>
            {client?.sector && typeof client.sector === 'string' && client.sector.trim() && (
              <p className="text-white/50 mt-1 flex items-center gap-2">
                <span className="inline-flex items-center px-3 py-1 bg-white/[0.06] text-white/50 rounded-lg text-sm font-medium">
                  {client.sector}
                </span>
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setIsEditModalOpen(true)}
            className="flex items-center gap-2 px-5 py-2.5 bg-white/[0.08] text-white/60 border border-white/[0.08] rounded-xl hover:bg-white/[0.14] transition-all font-medium"
          >
            <Pencil className="w-4 h-4" />
            Modifier
          </button>
          <button
            onClick={() => setIsDeleteModalOpen(true)}
            className="flex items-center gap-2 px-5 py-2.5 bg-red-500/20 text-red-400 border border-red-500/20 rounded-xl hover:bg-red-500/30 transition-all font-medium"
          >
            <Trash2 className="w-4 h-4" />
            Supprimer
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Informations principales */}
        <div className="lg:col-span-2 space-y-6">
          {/* Statistiques */}
          <div className="bg-white/[0.04] rounded-2xl border border-white/[0.08] p-6">
            <h2 className="text-xl font-semibold text-white mb-6">Aperçu</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white/[0.04] rounded-xl p-5 border border-white/[0.08]">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-[10px] font-semibold text-white/40 uppercase tracking-widest">Campagnes</h3>
                  <div className="w-10 h-10 rounded-lg bg-white/[0.06] flex items-center justify-center">
                    <FileText className="w-5 h-5 text-white/50" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-white">{collabs.length}</p>
              </div>
              <div className="bg-white/[0.04] rounded-xl p-5 border border-white/[0.08]">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-[10px] font-semibold text-white/40 uppercase tracking-widest">Influenceurs</h3>
                  <div className="w-10 h-10 rounded-lg bg-white/[0.06] flex items-center justify-center">
                    <Users className="w-5 h-5 text-white/50" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-white">
                  {new Set(collabs.flatMap(c => c.influencers.map(i => i.fullName))).size}
                </p>
              </div>
              <div className="bg-white/[0.04] rounded-xl p-5 border border-white/[0.08]">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-[10px] font-semibold text-white/40 uppercase tracking-widest">CA Total</h3>
                  <div className="w-10 h-10 rounded-lg bg-white/[0.06] flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-white/50" />
                  </div>
                </div>
                <p className="text-3xl font-bold text-white">{formatCurrency(getTotalRevenue())}</p>
              </div>
            </div>
          </div>

          {/* Contacts */}
          <div className="bg-white/[0.04] rounded-2xl border border-white/[0.08] p-6">
            <h2 className="text-xl font-semibold text-white mb-6">Contacts</h2>
            <div className="space-y-3">
              {client?.contacts.map((contact, index) => (
                <div key={index} className="bg-white/[0.04] rounded-xl p-5 border border-white/[0.08] hover:bg-white/[0.06] transition-all">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-white/[0.06] flex items-center justify-center flex-shrink-0">
                      {contact.type === 'brand' ? (
                        <Building2 className="w-5 h-5 text-white/40" />
                      ) : (
                        <User className="w-5 h-5 text-white/40" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-white font-semibold text-base">{contact.name}</span>
                        {contact.role && (
                          <span className="inline-flex items-center px-2.5 py-0.5 bg-white/[0.08] text-white/50 rounded-lg text-xs font-medium">
                            {contact.role}
                          </span>
                        )}
                      </div>
                      <div className="space-y-1.5 mt-2">
                        <div className="flex items-center gap-2 text-white/50 text-sm">
                          <Mail className="w-4 h-4 text-white/30" />
                          <a href={`mailto:${contact.email}`} className="hover:text-white transition-colors">
                            {contact.email}
                          </a>
                        </div>
                        {contact.phone && (
                          <div className="flex items-center gap-2 text-white/50 text-sm">
                            <Phone className="w-4 h-4 text-white/30" />
                            <a href={`tel:${contact.phone}`} className="hover:text-white transition-colors">
                              {contact.phone}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {(!client?.contacts || client.contacts.length === 0) && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-xl bg-white/[0.06] flex items-center justify-center mx-auto mb-3">
                    <User className="w-8 h-8 text-white/30" />
                  </div>
                  <p className="text-white/30 font-medium">Aucun contact enregistré</p>
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          {client?.notes && (
            <div className="bg-white/[0.04] rounded-2xl border border-white/[0.08] p-6">
              <h2 className="text-xl font-semibold text-white mb-4">Notes</h2>
              <div className="bg-white/[0.04] rounded-xl p-5 border border-white/[0.08]">
                <p className="text-white/60 whitespace-pre-wrap leading-relaxed">{client.notes}</p>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Informations */}
          <div className="bg-white/[0.04] rounded-2xl border border-white/[0.08] p-6">
            <h2 className="text-xl font-semibold text-white mb-5">Informations</h2>
            <div className="space-y-4">
              <div className="p-4 bg-white/[0.04] rounded-xl border border-white/[0.08]">
                <p className="text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-2">Date d'ajout</p>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-white/30" />
                  <p className="text-white font-medium">{formatDate(client?.created_at)}</p>
                </div>
              </div>
              <div className="p-4 bg-white/[0.04] rounded-xl border border-white/[0.08]">
                <p className="text-[10px] font-semibold text-white/40 uppercase tracking-widest mb-2">Dernière modification</p>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-white/30" />
                  <p className="text-white font-medium">{formatDate(client?.updated_at)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Campagnes récentes */}
          <div className="bg-white/[0.04] rounded-2xl border border-white/[0.08] p-6">
            <h2 className="text-xl font-semibold text-white mb-5">Campagnes récentes</h2>
            <div className="space-y-3">
              {collabs.slice(0, 5).map((collab) => {
                const status = getCampaignStatus(collab);
                return (
                  <div
                    key={collab.id}
                    className="bg-white/[0.04] rounded-xl p-4 border border-white/[0.08] hover:bg-white/[0.06] cursor-pointer transition-all group"
                    onClick={() => navigate(`/app/collaborations/${collab.id}`)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <span className="font-semibold text-white group-hover:text-white/80 transition-colors flex-1 pr-2">{collab.name}</span>
                      <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold border whitespace-nowrap ${status.className}`}>
                        {status.label}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-white/50 mb-2">
                      <Calendar className="w-4 h-4 text-white/30" />
                      <span>{formatDate(collab.date)}</span>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-white/[0.06]">
                      <div className="flex items-center gap-1.5">
                        <Users className="w-4 h-4 text-white/30" />
                        <span className="text-xs text-white/50 font-medium">
                          {collab.influencers?.length || 0} influenceur{(collab.influencers?.length || 0) > 1 ? 's' : ''}
                        </span>
                      </div>
                      <span className="text-sm font-bold text-white/70">
                        {formatCurrency(collab.influencers?.reduce((sum, inf) => sum + parseFloat(inf.rate || '0'), 0) || 0)}
                      </span>
                    </div>
                  </div>
                );
              })}
              {collabs.length === 0 && (
                <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-xl bg-white/[0.06] flex items-center justify-center mx-auto mb-3">
                    <FileText className="w-8 h-8 text-white/30" />
                  </div>
                  <p className="text-white/30 font-medium">Aucune campagne pour ce client</p>
                </div>
              )}
              {collabs.length > 5 && (
                <button
                  onClick={() => {/* Afficher toutes les campagnes */}}
                  className="w-full flex items-center justify-center gap-2 text-sm text-white/50 hover:text-white font-medium py-3 mt-2 bg-white/[0.06] hover:bg-white/[0.10] rounded-xl transition-all"
                >
                  <span>Voir toutes les campagnes ({collabs.length})</span>
                  <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <Dialog
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        className="fixed inset-0 z-50 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative bg-[#141414] border border-white/[0.08] rounded-2xl max-w-2xl w-full shadow-2xl">
            <div className="absolute top-5 right-5 z-10">
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="p-2 text-white/40 hover:text-white hover:bg-white/[0.08] rounded-xl transition-all"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="p-6 border-b border-white/[0.08]">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/[0.06] flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-white/60" />
                  </div>
                  <h2 className="text-2xl font-bold text-white">
                    Modifier le client
                  </h2>
                </div>
              </div>

              <div className="px-6 pb-6">

                <div className="space-y-5">
                  <div>
                    <label htmlFor="brand_name" className="block text-sm font-semibold text-white/60 mb-2">
                      Nom du client
                    </label>
                    <input
                      type="text"
                      id="brand_name"
                      value={formData.brand_name}
                      onChange={(e) => setFormData({ ...formData, brand_name: e.target.value })}
                      className="block w-full rounded-xl bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 px-4 py-2.5 focus:ring-1 focus:ring-white/20 outline-none transition-all"
                      placeholder="Nom de la marque ou de l'agence"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-white/60 mb-2">
                      Secteur
                    </label>
                    <select
                      value={formData.sector || ''}
                      onChange={(e) => setFormData({ ...formData, sector: e.target.value })}
                      className="w-full bg-white/[0.06] border border-white/[0.08] rounded-xl px-4 py-2.5 text-white focus:ring-1 focus:ring-white/20 outline-none transition-all"
                    >
                      <option value="">Sélectionner un secteur</option>
                      {BUSINESS_SECTORS.map((sector) => (
                        <option key={sector} value={sector}>{sector}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="notes" className="block text-sm font-semibold text-white/60 mb-2">
                      Notes
                    </label>
                    <textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      rows={3}
                      className="block w-full bg-white/[0.06] border border-white/[0.08] text-white placeholder-white/30 rounded-xl px-4 py-2.5 focus:ring-1 focus:ring-white/20 outline-none transition-all resize-none"
                      placeholder="Ajoutez des notes sur ce client..."
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <label className="block text-sm font-semibold text-white/60">
                        Contacts
                      </label>
                      <button
                        type="button"
                        onClick={handleAddContact}
                        className="flex items-center gap-2 px-3 py-1.5 bg-white/[0.08] text-white/60 border border-white/[0.08] rounded-xl hover:bg-white/[0.14] transition-all text-sm font-medium"
                      >
                        <Plus className="w-4 h-4" />
                        Ajouter
                      </button>
                    </div>
                    <div className="space-y-3">
                      {formData.contacts?.map((contact, index) => (
                        <div key={index} className="p-4 bg-white/[0.04] rounded-xl border border-white/[0.08]">
                          <div className="grid grid-cols-2 gap-3">
                            <input
                              type="text"
                              value={contact.name}
                              onChange={(e) => handleContactChange(index, 'name', e.target.value)}
                              placeholder="Nom"
                              className="w-full bg-white/[0.06] border border-white/[0.08] rounded-xl px-3 py-2 text-white placeholder-white/30 focus:ring-1 focus:ring-white/20 outline-none transition-all"
                              required
                            />
                            <input
                              type="email"
                              value={contact.email}
                              onChange={(e) => handleContactChange(index, 'email', e.target.value)}
                              placeholder="Email"
                              className="w-full bg-white/[0.06] border border-white/[0.08] rounded-xl px-3 py-2 text-white placeholder-white/30 focus:ring-1 focus:ring-white/20 outline-none transition-all"
                              required
                            />
                            <input
                              type="tel"
                              value={contact.phone}
                              onChange={(e) => handleContactChange(index, 'phone', e.target.value)}
                              placeholder="Téléphone"
                              className="w-full bg-white/[0.06] border border-white/[0.08] rounded-xl px-3 py-2 text-white placeholder-white/30 focus:ring-1 focus:ring-white/20 outline-none transition-all"
                            />
                            <select
                              value={contact.type}
                              onChange={(e) => handleContactChange(index, 'type', e.target.value)}
                              className="w-full bg-white/[0.06] border border-white/[0.08] rounded-xl px-3 py-2 text-white focus:ring-1 focus:ring-white/20 outline-none transition-all"
                            >
                              <option value="brand">Marque</option>
                              <option value="agency">Agence</option>
                            </select>
                            <div className="col-span-2">
                              <input
                                type="text"
                                value={contact.role || ''}
                                onChange={(e) => handleContactChange(index, 'role', e.target.value)}
                                placeholder="Rôle (ex: Directeur Marketing)"
                                className="w-full bg-white/[0.06] border border-white/[0.08] rounded-xl px-3 py-2 text-white placeholder-white/30 focus:ring-1 focus:ring-white/20 outline-none transition-all"
                              />
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveContact(index)}
                            className="mt-3 p-2 text-red-400/60 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                            title="Supprimer ce contact"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-6 border-t border-white/[0.08]">
                  <button
                    type="button"
                    onClick={() => setIsEditModalOpen(false)}
                    className="px-5 py-2.5 text-sm font-medium text-white/60 bg-white/[0.08] border border-white/[0.08] rounded-xl hover:bg-white/[0.14] transition-all"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2.5 text-sm font-semibold text-black bg-white rounded-xl hover:bg-white/90 transition-all"
                  >
                    Enregistrer
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        className="fixed inset-0 z-50 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative bg-[#141414] border border-white/[0.08] rounded-2xl max-w-md w-full shadow-2xl">
            <div className="p-6">
              <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center mb-4">
                <Trash2 className="w-6 h-6 text-red-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Confirmer la suppression</h3>
              <p className="text-white/50 leading-relaxed">
                Êtes-vous sûr de vouloir supprimer le client <span className="font-semibold text-white">"{client?.brand_name}"</span> ? Cette action est irréversible.
              </p>
              {collabs.length > 0 && (
                <div className="mt-4 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-xl">
                  <p className="text-sm text-yellow-400 leading-relaxed">
                    <strong className="font-semibold">Attention :</strong> Ce client est associé à {collabs.length} collaboration{collabs.length > 1 ? 's' : ''}.
                    La suppression n'affectera pas les collaborations existantes.
                  </p>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-white/[0.08]">
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(false)}
                className="px-5 py-2.5 text-sm font-medium text-white/60 bg-white/[0.08] border border-white/[0.08] rounded-xl hover:bg-white/[0.14] transition-all"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="px-5 py-2.5 text-sm font-medium text-red-400 bg-red-500/20 border border-red-500/20 rounded-xl hover:bg-red-500/30 transition-all"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
}